import React, {useEffect, useRef, useState} from 'react';
import {withTranslation} from 'react-i18next';
import './multipleChoiceOptions.css';

export const CheckboxOptions = ({options, questionId, onOptionsChanged, t}) => {
  const initialOptions = () => options.options || [`${t('Option')} 1`];
  const [values, setValues] = useState(initialOptions);
  const [minSelected, setMinSelected] = useState(options.minSelected ?? 1);
  const [maxSelected, setMaxSelected] = useState(options.maxSelected ?? initialOptions().length);
  const nextFocus = useRef(null);

  useEffect(() => {
    const nextValues = options.options || [`${t('Option')} 1`];
    setValues(nextValues);
    setMinSelected(options.minSelected ?? 1);
    setMaxSelected(options.maxSelected ?? nextValues.length);
  }, [options]);

  useEffect(() => {
    onOptionsChanged({
      options: initialOptions(),
      minSelected: options.minSelected ?? 1,
      maxSelected: options.maxSelected ?? initialOptions().length
    });
  }, []);

  useEffect(() => {
    if (nextFocus.current === null) return;
    const input = document.getElementById(`${questionId}-${nextFocus.current}`);
    input?.focus();
    input?.select();
    nextFocus.current = null;
  }, [values.length, questionId]);

  const publish = (nextValues, nextMin = minSelected, nextMax = maxSelected) => {
    const boundedMax = Math.min(nextMax, nextValues.length);
    const boundedMin = Math.min(nextMin, boundedMax);
    setValues(nextValues);
    setMinSelected(boundedMin);
    setMaxSelected(boundedMax);
    onOptionsChanged({
      options: nextValues,
      minSelected: boundedMin,
      maxSelected: boundedMax
    });
  };

  const changeOption = (index, value) => {
    const updatedOptions = [...values];
    updatedOptions[index] = value;
    publish(updatedOptions);
  };

  const addOption = () => {
    nextFocus.current = values.length;
    publish([...values, `${t('Option')} ${values.length + 1}`]);
  };

  const removeOption = index => publish(values.filter((_, optionIndex) => optionIndex !== index));

  return <div className="br-survey-option-editor">
    <div className="br-survey-option-editor__limits">
      <label>{t('SelectAtLeast')}
        <select value={minSelected} onChange={event => publish(values, Number(event.target.value))}>
          {[0, 1].map(value => <option key={value} value={value}>{value}</option>)}
        </select>
      </label>
      <label>{t('SelectAtMost')}
        <select value={maxSelected} onChange={event => publish(values, minSelected, Number(event.target.value))}>
          {values.map((_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}
        </select>
      </label>
    </div>
    {values.map((option, index) => <div className="br-survey-option-editor__row" key={index}>
      <span aria-hidden="true" className="br-survey-option-editor__marker">□</span>
      <input id={`${questionId}-${index}`} type="text" value={option}
        aria-label={`${t('Option')} ${index + 1}`}
        onChange={event => changeOption(index, event.target.value)} />
      {values.length > 1 && <button type="button" aria-label={`${t('Remove')} ${t('Option')} ${index + 1}`}
        onClick={() => removeOption(index)}>×</button>}
    </div>)}
    <button className="br-survey-option-editor__add" type="button" onClick={addOption}>
      {t('Add')} {t('Option')}
    </button>
  </div>;
};

export default withTranslation()(CheckboxOptions);
